import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ResourceSidebar from '../Shared/ResourceSidebar';
import { contactsService } from '../../services/contactsService';
import '../Venues/VenuesPage.css';
import './ContactsPage.css';

const emptyForm = { first_name: '', last_name: '', email: '', phone: '' };
const pageSize = 100;
const eventTitle = (event) => {
  const lines = event.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const marker = lines.indexOf('Event:');
  return marker >= 0 && lines[marker + 1] ? lines[marker + 1] : lines[0] || event;
};
const errorMessage = (error) => {
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' ? detail : 'Unable to save or load contacts. Please check your entries and try again.';
};

export default function ContactsPage() {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(null);
  const [eventEntries, setEventEntries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [notice, setNotice] = useState('');
  const firstNameRef = useRef(null);
  const addButtonRef = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    contactsService.list({ q: search || undefined, limit: pageSize, offset })
      .then((data) => { if (active) { setContacts(data.items); setTotal(data.total_count); } })
      .catch((err) => { if (active) setError(errorMessage(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [search, offset, refresh]);

  useEffect(() => { if (showForm) firstNameRef.current?.focus(); }, [showForm]);

  const closeForm = () => { setShowForm(false); setEditing(null); setEventEntries([]); setForm(emptyForm); setSaveError(''); addButtonRef.current?.focus(); };
  const editContact = (contact) => {
    setEditing(contact);
    setForm({ first_name: contact.first_name || '', last_name: contact.last_name || '', email: contact.email || '', phone: contact.phone || '' });
    setEventEntries([...contact.events]);
    setSaveError(''); setNotice(''); setShowForm(true);
  };
  const saveContact = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setSaveError('');
    try {
      if (editing) {
        const changes = {};
        for (const key of ['first_name', 'last_name', 'email', 'phone']) {
          if (form[key] !== (editing[key] || '')) changes[key] = form[key].trim() || null;
        }
        if (JSON.stringify(eventEntries) !== JSON.stringify(editing.events)) {
          changes.events = eventEntries.map((value) => value.trim()).filter(Boolean);
        }
        await contactsService.update(editing.contact_id, changes);
      } else await contactsService.create({
        first_name: form.first_name.trim(), last_name: form.last_name.trim(),
        events: eventEntries.map((value) => value.trim()).filter(Boolean),
        email: form.email.trim() || null, phone: form.phone.trim() || null,
      });
      closeForm();
      if (!editing) { setQuery(''); setSearch(''); setOffset(0); }
      setRefresh((value) => value + 1);
      setNotice(editing ? 'Contact updated successfully.' : 'Contact added successfully.');
    } catch (err) { setSaveError(errorMessage(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="venues-page resource-page-with-sidebar contacts-page">
      <ResourceSidebar />
      <header className="venues-header"><div className="venues-header-content">
        <h1>Contacts</h1>
        <div className="venues-header-actions">
        <button ref={addButtonRef} type="button" className="btn btn-primary" disabled={showForm} onClick={() => { setNotice(''); setEventEntries(['']); setShowForm(true); }}>New contact</button>
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/user')}>Back to Dashboard</button>
        </div>
      </div></header>
      <main className="venues-main">
        {notice && <p role="status">{notice}</p>}
        {showForm && <section className="contacts-create" aria-labelledby="contact-form-title">
          <h2 id="contact-form-title">{editing ? 'Edit contact' : 'New contact'}</h2>
          <form onSubmit={saveContact}>
            <fieldset disabled={saving}>
              <div className="contacts-fields">
                {[['first_name', 'First name', 'text', 255], ['last_name', 'Last name', 'text', 255], ['email', 'Email', 'email', 320], ['phone', 'Phone', 'tel', 100]].map(([key, label, type, maxLength]) => (
                  <label key={key} htmlFor={`contact-${key}`}>{label}{!editing && key.endsWith('name') ? ' *' : ''}
                    <input id={`contact-${key}`} ref={key === 'first_name' ? firstNameRef : undefined} type={editing && key === 'email' && form.email === (editing.email || '') ? 'text' : type} maxLength={maxLength} required={!editing && key.endsWith('name')} pattern={key.endsWith('name') ? '.*\\S.*' : undefined} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
                  </label>
                ))}
              </div>
              <div className="contacts-event-editor">
                <h3>Events</h3>
                <p>Each box is one event. Booking details can span multiple lines.</p>
                {eventEntries.map((value, index) => <div key={index}>
                  <label htmlFor={`contact-event-${index}`}>Event {index + 1}</label>
                  <textarea id={`contact-event-${index}`} rows={4} maxLength={10000} value={value} onChange={(e) => setEventEntries(eventEntries.map((entry, position) => position === index ? e.target.value : entry))} />
                  <button type="button" className="btn btn-secondary" aria-label={`Remove event ${index + 1}`} onClick={() => setEventEntries(eventEntries.filter((entry, position) => position !== index))}>Remove event</button>
                </div>)}
                <button type="button" className="btn btn-secondary" disabled={eventEntries.length >= 100} onClick={() => setEventEntries([...eventEntries, ''])}>Add event</button>
              </div>
              {saveError && <p role="alert">{saveError}</p>}
              <div className="contacts-actions">
                <button className="btn btn-primary" type="submit">{saving ? 'Saving…' : editing ? 'Save changes' : 'Save contact'}</button>
                <button className="btn btn-secondary" type="button" onClick={closeForm}>Cancel</button>
              </div>
            </fieldset>
          </form>
        </section>}
        <form className="venues-filter-form" onSubmit={(e) => { e.preventDefault(); setSearch(query.trim()); setOffset(0); setRefresh((value) => value + 1); }}>
          <div className="form-group"><label htmlFor="contacts-search">Search contacts</label>
            <input id="contacts-search" type="search" maxLength={500} placeholder="Name, event, email or phone" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div><button className="btn btn-secondary" type="submit">Search</button>
        </form>
        {loading ? <p role="status">Loading contacts…</p> : error ? <p role="alert">{error}</p> : <>
          <p>{total} {total === 1 ? 'contact' : 'contacts'}</p>
          {!contacts.length ? <p>{search ? 'No contacts match your search.' : 'No contacts yet. Add your first contact using New contact.'}</p> : <div className="contacts-table-wrap">
            <table className="contacts-table"><caption className="contacts-caption">Contact directory</caption>
              <thead><tr>{['First name', 'Last name', 'Events', 'Email', 'Phone', 'Actions'].map((label) => <th scope="col" key={label}>{label}</th>)}</tr></thead>
              <tbody>{contacts.map((contact) => <tr key={contact.contact_id}>
                <td>{contact.first_name || '—'}</td><td>{contact.last_name || '—'}</td>
                <td><div className="contact-events">{contact.events.length ? contact.events.map((name) => name.includes('\n') ? <details className="contact-event" key={name}><summary>{eventTitle(name)}</summary><div className="contact-event-details">{name}</div></details> : <span className="contact-event" key={name}>{name}</span>) : <span>—</span>}</div></td>
                <td>{contact.email ? <a href={`mailto:${contact.email}`}>{contact.email}</a> : '—'}</td>
                <td>{contact.phone || '—'}</td>
                <td><button type="button" className="btn btn-secondary" disabled={showForm} onClick={() => editContact(contact)}>Edit</button></td>
              </tr>)}</tbody>
            </table>
          </div>}
          {total > pageSize && <nav className="contacts-actions" aria-label="Contact pages">
            <button className="btn btn-secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - pageSize))}>Previous</button>
            <span>{offset + 1}–{Math.min(offset + pageSize, total)} of {total}</span>
            <button className="btn btn-secondary" disabled={offset + pageSize >= total} onClick={() => setOffset(offset + pageSize)}>Next</button>
          </nav>}
        </>}
      </main>
    </div>
  );
}
